import externalAPI from '../../Connect/ExternalAPI';
import apiService from '../../Services/APIService';
import { checkAPIResponse } from '../../Utils/APIResponseUtils';
import { getAuthData } from '../../Utils/AuthUtils';
import { resolveTaskAgencyID } from '../../Utils/Utilities';
import { getUserConfig } from '../../Store/Reducers';

// Unified function for add, update, delete task
export const manageTaskAPI = async (taskData, operation = 'ADD') => {
    let contextSet = false;
    try {
        const authData = getAuthData();
        const currentUserID = authData?.UserID ? String(authData.UserID) : "";
        const activeAgencyID = Number(authData?.AgencyID || 0);
        const isDelete = operation === 'DELETE';

        const taskAgencyID = resolveTaskAgencyID(taskData, activeAgencyID);
        if (taskAgencyID && apiService?.agencyContext?.forAgencyID !== taskAgencyID) {
            apiService.setAgencyContext(taskAgencyID);
            contextSet = true;
        }

        let myCategory = taskData.myCategory || 0;
        if (myCategory) {
            try {
                const uConfig = getUserConfig();
                const taskCtgs = JSON.parse(uConfig?.TaskCtgs || '[]');
                const matchedCategory = taskCtgs.find(c => Number(c.CID) === Number(myCategory));
                const isDeleted = !matchedCategory || matchedCategory.IsD === 1 || matchedCategory.IsD === true || matchedCategory.isD === 1 || matchedCategory.isD === true;
                if (isDeleted) {
                    myCategory = 0;
                }
            } catch (err) {
                console.error('Failed to validate myCategory:', err);
            }
        }

        const preparedTaskData = isDelete
            ? {
                changeType: 'DELETE',
                taskID: taskData.taskID || 0
            }
            : {
                changeType: operation,
                connID: taskData.connID || 0,
                taskName: taskData.taskName || '',
                description: taskData.description || '',
                dueDT: taskData.dueDT || '',
                pocUserID: taskData.pocUserID || currentUserID,
                priority: taskData.priority || '',
                threads: Array.isArray(taskData.threads)
                    ? taskData.threads.join(',')
                    : (taskData.threads || ''),
                status: taskData.status || 1,
                taskType: taskData.taskType || '',
                taskID: taskData.taskID || 0,
                contractIDs: Array.isArray(taskData.contracts)
                    ? taskData.contracts.join(',')
                    : (taskData.contracts || ''),
                secConnIDs: Array.isArray(taskData.secConnIDs)
                    ? taskData.secConnIDs.join(',')
                    : (taskData.secConnIDs || ''),
                rmdrDT: taskData.scheduleDT || '',
                isRmdrUpdate: taskData.isRmdrUpdate !== undefined ? taskData.isRmdrUpdate : 1,
                shareByEmail: taskData.shareByEmail || false,
                email: taskData.email || '',
                agencyName: taskData.agencyName || '',
                usrCtgCID: myCategory,
                taskAgencyID: taskAgencyID || (Number(taskData.taskAgencyID) || 0) || activeAgencyID || -1,
                canComplete: taskData.canComplete != false,
                statusNote: taskData.statusNote || '',
                repeatAfter: taskData.repeatAfter || 0,
                repeatDT: taskData.repeatDT || '',
                pocTask: taskData.pocTask || '',
                pocPriority: taskData.pocPriority || 'M',
                class: taskData.taskClass || 1,
                options: taskData.options || null,
                observerUserIDs: taskData.observerUserIDs || ''
            };

        const result = await apiService.sendChange(5, 'set', preparedTaskData);
        checkAPIResponse(result);

        return result;
    } catch (error) {
        console.error(`${operation} task API error:`, error);
        throw error;
    } finally {
        if (contextSet) {
            apiService.clearAgencyContext();
        }
    }
};

// Function for task actions (Complete/Cancel/Remind)
export const taskActionAPI = async (taskData, action) => {
    let contextSet = false;
    try {
        const authData = getAuthData();
        const currentUserID = authData?.UserID ? String(authData.UserID) : "";
        const activeAgencyID = Number(authData?.AgencyID || 0);

        const taskAgencyID = resolveTaskAgencyID(taskData, activeAgencyID);

        if (taskAgencyID && apiService?.agencyContext?.forAgencyID !== taskAgencyID) {
            apiService.setAgencyContext(taskAgencyID);
            contextSet = true;
        }


        // Validate action type
        if (!['COMPLETE', 'CANCEL', 'REJECT', 'RMDR', 'MRKDONE', 'STATUS', 'REOPEN'].includes(action)) {
            throw new Error('Invalid action type.');
        }

        // Validate required fields
        if (!taskData.taskID) {
            throw new Error('Task ID is required for task actions');
        }

        const preparedActionData = {
            changeType: action,
            taskID: taskData.taskID,
            connID: taskData.connID,
            pocUserID: taskData.pocUserID || currentUserID,
            taskName: taskData.taskName || '',
            description: taskData.description || '',
            dueDT: taskData.dueDT || '',
            priority: taskData.priority || '',
            status: taskData.status || 1,
            taskType: taskData.taskType || '',
            threads: (Array.isArray(taskData.threads) ? taskData.threads.join(',') : taskData.threads) || '',
            contracts: (Array.isArray(taskData.contracts) ? taskData.contracts.join(',') : taskData.contracts) || '',
            recType: '',
            recDT: '',
            recNotes: '',
            recLat: '',
            recLon: '',
            currStageID: 1,
            reason: '',
            recSecConnIDs: '',
            statusNote: taskData.statusNote || '',
            repeatTask: taskData.repeatTask || false,
            selectedOptionID: taskData.selectedOptionID || 0
        };

        const result = await apiService.sendChange(5, 'action', preparedActionData);
        checkAPIResponse(result);

        return result;
    } catch (error) {
        console.error(`Task ${action} action API error:`, error);
        throw error;
    } finally {
        if (contextSet) {
            apiService.clearAgencyContext();
        }
    }
};

// Helper functions for specific actions
export const completeTaskAPI = async (taskData) => {
    return await taskActionAPI(taskData, 'COMPLETE');
};

export const cancelTaskAPI = async (taskData) => {
    return await taskActionAPI(taskData, 'CANCEL');
};

export const rejectTaskAPI = async (taskData) => {
    return await taskActionAPI(taskData, 'REJECT');
};

export const remindTaskAPI = async (taskData) => {
    return await taskActionAPI(taskData, 'RMDR');
};

export const reopenTaskAPI = async (taskData) => {
    return await taskActionAPI(taskData, 'REOPEN');
};

export const statusUpdateAPI = async (taskData) => {
    return await taskActionAPI(taskData, 'STATUS');
};

// Mark as Done functionality is disabled for now (being reworked into a Status update flow).
// export const markAsDoneAPI = async (taskData) => {
//     return await taskActionAPI(taskData, 'MRKDONE');
// };

export const bulkTaskActionAPI = async (taskArrIDs, changeType, pocUserID = 0) => {
    try {
        const payload = {
            changeType: changeType,
            tskIDs: JSON.stringify(taskArrIDs.map((tskID) => ({ ID: tskID }))),
            pocUserID: pocUserID
        };
        const result = await apiService.sendChange(5, 'setBulkAction', payload);
        checkAPIResponse(result);
        return result;
    } catch (error) {
        console.error(`Bulk task action API error (${changeType}):`, error);
        throw error;
    }
};

export const getTaskHistoryAPI = async ({ prdType = 'ALL', prdQty = 0, year = 0, month = 0 } = {}) => {
    try {
        const payload = {
            prdType: prdType || 'ALL',
            prdQty: prdQty || 0,
            year: year || 0,
            month: month || 0
        };
        const result = await apiService.getData(5, 'getTskHistory', payload);
        checkAPIResponse(result);

        if (result?.updatePackage && typeof result.updatePackage === 'string') {
            try {
                return JSON.parse(result.updatePackage);
            } catch (parseError) {
                console.error('Error parsing task history updatePackage:', parseError);
                return { Table1: [] };
            }
        }

        return result;
    } catch (error) {
        console.error('Get task history API error:', error);
        throw error;
    }
};

export const getExternalTaskDetailsAPI = async (agencyID, taskID) => {
    try {
        const payload = {
            agencyID: agencyID,
            taskID: taskID,
            Type: 'TASK'
        };

        const result = await externalAPI.getData(5, 'get', payload);
        checkAPIResponse(result);

        return result;
    } catch (error) {
        console.error('External task details API error:', error);
        throw error;
    }
};